import { useState } from 'react'
import { useBusinessAreas, useCreateBusinessArea, useUpdateBusinessArea, useDeleteBusinessArea } from '@/hooks/use-business-areas'
import { Modal } from '@/components/modal'
import type { BusinessArea } from '@/types'

interface AreaFormData {
  name: string
  display_order: number
}

export function AdminBusinessAreas() {
  const { data: areas, isLoading } = useBusinessAreas()
  const createArea = useCreateBusinessArea()
  const updateArea = useUpdateBusinessArea()
  const deleteArea = useDeleteBusinessArea()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingArea, setEditingArea] = useState<BusinessArea | null>(null)
  const [formData, setFormData] = useState<AreaFormData>({
    name: '',
    display_order: 0,
  })

  const openCreate = () => {
    setEditingArea(null)
    setFormData({ name: '', display_order: (areas?.length || 0) + 1 })
    setIsModalOpen(true)
  }

  const openEdit = (area: BusinessArea) => {
    setEditingArea(area)
    setFormData({
      name: area.name,
      display_order: area.display_order,
    })
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (editingArea) {
      await updateArea.mutateAsync({ id: editingArea.id, ...formData })
    } else {
      await createArea.mutateAsync(formData)
    }
    setIsModalOpen(false)
  }

  const handleDelete = async (id: string) => {
    if (confirm('Delete this business area? Metrics using this area will have their area cleared.')) {
      await deleteArea.mutateAsync(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Areas</h1>
          <p className="text-sm text-gray-500 mt-1">Categorize metrics by area of business</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary text-sm px-3 py-1.5">
          + Add Area
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Order</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {areas?.map((area) => (
              <tr key={area.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-500">{area.display_order}</td>
                <td className="px-6 py-4 font-medium text-gray-900">{area.name}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    area.is_active ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {area.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => openEdit(area)} className="text-brand-teal hover:text-brand-teal/80">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(area.id)} className="text-danger-600 hover:text-danger-700">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!areas || areas.length === 0) && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No business areas defined yet. Create your first area to categorize metrics.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingArea ? 'Edit Business Area' : 'Create Business Area'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
              placeholder="e.g., Sales, Marketing, Operations"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              min="1"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
              className="w-24 rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={createArea.isPending || updateArea.isPending}>
              {editingArea ? 'Save Changes' : 'Create Area'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
